/**
 * GradeSettings
 * =============
 * Editable grade bands: text grade name + min/max percentage range.
 * Also lets the administrator define custom class groups (e.g. Foundation,
 * Primary) used by the class-group summary sheet in the Excel export.
 */

import React, { useState, useEffect } from 'react';
import {
  fetchGrades, saveGrades, fetchClassGroups, saveClassGroups,
  fetchClassOptions, fetchCoScholastic, saveCoScholastic,
  fetchSignatures, saveSignatures,
} from './resultsApi';

const MAX_SIGNATURES = 4;

// human hint for how N signatures are placed on the card
const SIG_LAYOUTS = ['none', 'right', 'left + right', 'left + center + right', 'spread left to right'];

const GradeSettings = ({ schoolId }) => {
  const [grades, setGrades] = useState([]);
  const [groups, setGroups] = useState([]);
  const [areas, setAreas] = useState([]);
  const [sigs, setSigs] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [openPicker, setOpenPicker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingGroups, setSavingGroups] = useState(false);
  const [savingAreas, setSavingAreas] = useState(false);
  const [savingSigs, setSavingSigs] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [gradeData, groupData, classData, areaData, sigData] = await Promise.all([
          fetchGrades(schoolId),
          fetchClassGroups(schoolId).catch(() => ({ groups: [] })),
          fetchClassOptions(schoolId).catch(() => ({ options: [] })),
          fetchCoScholastic(schoolId).catch(() => ({ areas: [] })),
          fetchSignatures(schoolId).catch(() => ({ signatures: [] })),
        ]);
        if (active) {
          setGrades(gradeData.grades || []);
          setGroups(
            (groupData.groups || []).map((g) => ({
              name: g.name,
              classes: g.classes || [],
            })),
          );
          setAreas(areaData.areas || []);
          setSigs((sigData.signatures || []).map((s) => ({
            label: s.label || '',
            blob: s.blob || '',
            url: s.url || '',
            border: Boolean(s.border),
            newImage: null,
          })));
          const labels = classData.options || [];
          setClassOptions([...new Set(labels)].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })));
        }
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [schoolId]);

  // close class picker when clicking elsewhere
  useEffect(() => {
    if (openPicker === null) return undefined;
    const onDown = (e) => {
      if (!e.target.closest('.class-picker')) setOpenPicker(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [openPicker]);

  const updateRow = (idx, field, value) => {
    setGrades((prev) => prev.map((g, i) => (i === idx ? { ...g, [field]: value } : g)));
  };

  const addRow = () => setGrades((prev) => [...prev, { grade: '', min: 0, max: 0, remark: '' }]);

  const removeRow = (idx) => setGrades((prev) => prev.filter((_, i) => i !== idx));

  const updateArea = (idx, value) => {
    setAreas((prev) => prev.map((a, i) => (i === idx ? value : a)));
  };

  const addArea = () => setAreas((prev) => [...prev, '']);

  const removeArea = (idx) => setAreas((prev) => prev.filter((_, i) => i !== idx));

  const handleSaveAreas = async () => {
    setError('');
    setMessage('');
    setSavingAreas(true);
    try {
      const payload = areas.map((a) => a.trim()).filter(Boolean);
      const data = await saveCoScholastic(schoolId, payload);
      setAreas(data.areas || payload);
      setMessage('Co-scholastic areas saved');
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingAreas(false);
    }
  };

  const updateSig = (idx, field, value) => {
    setSigs((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const addSig = () => setSigs((prev) => (prev.length >= MAX_SIGNATURES ? prev : [...prev, { label: '', blob: '', url: '', border: false, newImage: null }]));

  const removeSig = (idx) => setSigs((prev) => prev.filter((_, i) => i !== idx));

  const moveSig = (idx, dir) => {
    setSigs((prev) => {
      const to = idx + dir;
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[to]] = [next[to], next[idx]];
      return next;
    });
  };

  const handleSigFile = (idx, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Signature must be an image file'); return; }
    if (file.size > 2 * 1024 * 1024) { setError('Signature image must be under 2MB'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      updateSig(idx, 'newImage', { base64: reader.result, name: file.name, type: file.type });
    };
    reader.readAsDataURL(file);
  };

  const handleSaveSigs = async () => {
    setError('');
    setMessage('');

    const payload = [];
    for (const s of sigs) {
      const label = s.label.trim();
      if (!label) { setError('Every signature needs a label'); return; }
      const entry = { label, blob: s.blob, border: s.border };
      if (s.newImage) {
        entry.imageBase64 = s.newImage.base64;
        entry.imageName = s.newImage.name;
        entry.contentType = s.newImage.type;
      }
      payload.push(entry);
    }

    setSavingSigs(true);
    try {
      const data = await saveSignatures(schoolId, payload);
      const fresh = await fetchSignatures(schoolId).catch(() => ({ signatures: data.signatures || [] }));
      setSigs((fresh.signatures || []).map((s) => ({
        label: s.label || '',
        blob: s.blob || '',
        url: s.url || '',
        border: Boolean(s.border),
        newImage: null,
      })));
      setMessage('Signatures saved');
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingSigs(false);
    }
  };

  const updateGroup = (idx, field, value) => {
    setGroups((prev) => prev.map((g, i) => (i === idx ? { ...g, [field]: value } : g)));
  };

  const toggleGroupClass = (idx, label) => {
    setGroups((prev) => prev.map((g, i) => {
      if (i !== idx) return g;
      const has = g.classes.includes(label);
      return { ...g, classes: has ? g.classes.filter((c) => c !== label) : [...g.classes, label] };
    }));
  };

  const addGroup = () => setGroups((prev) => [...prev, { name: '', classes: [] }]);

  const removeGroup = (idx) => {
    setOpenPicker(null);
    setGroups((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSaveGroups = async () => {
    setError('');
    setMessage('');

    const payload = [];
    for (const g of groups) {
      const name = g.name.trim();
      const classes = g.classes.map((c) => c.trim()).filter(Boolean);
      if (!name && !classes.length) continue; // ignore fully-empty rows
      if (!name) { setError('Every class group needs a name'); return; }
      if (!classes.length) { setError(`Group "${name}" needs at least one class`); return; }
      payload.push({ name, classes });
    }

    setSavingGroups(true);
    try {
      const data = await saveClassGroups(schoolId, payload);
      setGroups((data.groups || payload).map((g) => ({
        name: g.name,
        classes: g.classes || [],
      })));
      setMessage('Class groups saved');
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingGroups(false);
    }
  };

  const handleSave = async () => {
    setError('');
    setMessage('');

    for (const g of grades) {
      if (!String(g.grade).trim()) { setError('Every grade needs a name'); return; }
      const min = Number(g.min);
      const max = Number(g.max);
      if (Number.isNaN(min) || Number.isNaN(max) || min > max) {
        setError(`Invalid range for grade "${g.grade}"`);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = grades.map((g) => ({
        grade: String(g.grade).trim(),
        min: Number(g.min),
        max: Number(g.max),
        remark: String(g.remark || '').trim(),
      }));
      const data = await saveGrades(schoolId, payload);
      setGrades(data.grades || payload);
      setMessage('Grades saved');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="results-loading">
        <div className="dash-spinner" />
        <span>Loading grades...</span>
      </div>
    );
  }

  return (
    <div className="grade-settings">
      <p className="grade-settings-help">
        Define grade bands by percentage. A student's grade is the band whose range contains their percentage.
      </p>

      {error && (
        <div className="results-error">
          <span>{error}</span>
          <button onClick={() => setError('')}>&times;</button>
        </div>
      )}
      {message && <div className="grade-settings-ok">{message}</div>}

      <div className="grade-table-wrap">
        <table className="grade-table">
          <thead>
            <tr>
              <th>Grade Name</th>
              <th>Min %</th>
              <th>Max %</th>
              <th>Remark (printed on report card)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {grades.map((g, idx) => (
              <tr key={idx}>
                <td>
                  <input
                    className="results-input grade-input"
                    type="text"
                    placeholder="e.g. A+"
                    value={g.grade}
                    onChange={(e) => updateRow(idx, 'grade', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="results-input grade-input grade-input-num"
                    type="number"
                    value={g.min}
                    onChange={(e) => updateRow(idx, 'min', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="results-input grade-input grade-input-num"
                    type="number"
                    value={g.max}
                    onChange={(e) => updateRow(idx, 'max', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="results-input grade-input grade-input-wide"
                    type="text"
                    placeholder="e.g. Outstanding performance. Keep it up!"
                    value={g.remark || ''}
                    onChange={(e) => updateRow(idx, 'remark', e.target.value)}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="grade-remove-btn"
                    onClick={() => removeRow(idx)}
                    title="Remove"
                  >
                    &times;
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grade-settings-actions">
        <button type="button" className="grade-add-btn" onClick={addRow}>
          + Add Grade
        </button>
        <button type="button" className="grade-save-btn" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Grades'}
        </button>
      </div>

      <h3 className="grade-settings-subhead">Class Groups</h3>
      <p className="grade-settings-help">
        Group classes for the “Class Groups” summary sheet in the class-wise Excel export
        (e.g. Foundation Stage = Nur, LKG, UKG). Pick the classes for each group from the list.
      </p>

      <div className="grade-table-wrap">
        <table className="grade-table">
          <thead>
            <tr>
              <th>Group Name</th>
              <th>Classes</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 && (
              <tr>
                <td colSpan="3" className="grade-groups-empty">No class groups configured yet.</td>
              </tr>
            )}
            {groups.map((g, idx) => (
              <tr key={idx}>
                <td>
                  <input
                    className="results-input grade-input"
                    type="text"
                    placeholder="e.g. Primary"
                    value={g.name}
                    onChange={(e) => updateGroup(idx, 'name', e.target.value)}
                  />
                </td>
                <td>
                  <div className="class-picker">
                    <div className="class-chips">
                      {g.classes.map((c) => (
                        <span key={c} className="class-chip">
                          {c}
                          <button
                            type="button"
                            className="class-chip-x"
                            onClick={() => toggleGroupClass(idx, c)}
                            title="Remove"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                      <button
                        type="button"
                        className="class-picker-btn"
                        onClick={() => setOpenPicker(openPicker === idx ? null : idx)}
                      >
                        {g.classes.length ? '+ Add' : 'Select classes'} ▾
                      </button>
                    </div>
                    {openPicker === idx && (
                      <div className="class-picker-panel">
                        {classOptions.length === 0 && (
                          <div className="class-picker-empty">No classes found</div>
                        )}
                        {classOptions.map((opt) => (
                          <label key={opt} className="class-picker-option">
                            <input
                              type="checkbox"
                              checked={g.classes.includes(opt)}
                              onChange={() => toggleGroupClass(idx, opt)}
                            />
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  <button
                    type="button"
                    className="grade-remove-btn"
                    onClick={() => removeGroup(idx)}
                    title="Remove"
                  >
                    &times;
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grade-settings-actions">
        <button type="button" className="grade-add-btn" onClick={addGroup}>
          + Add Group
        </button>
        <button type="button" className="grade-save-btn" onClick={handleSaveGroups} disabled={savingGroups}>
          {savingGroups ? 'Saving...' : 'Save Class Groups'}
        </button>
      </div>

      <h3 className="grade-settings-subhead">Co-Scholastic Areas</h3>
      <p className="grade-settings-help">
        Areas printed in the “Co-Scholastic” section of the report card
        (e.g. Art Education, Work Education, Discipline). Grades for these are filled
        in by the class teacher on the printed card.
      </p>

      <div className="grade-table-wrap">
        <table className="grade-table">
          <thead>
            <tr>
              <th>Area Name</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {areas.length === 0 && (
              <tr>
                <td colSpan="2" className="grade-groups-empty">No co-scholastic areas configured yet.</td>
              </tr>
            )}
            {areas.map((a, idx) => (
              <tr key={idx}>
                <td>
                  <input
                    className="results-input grade-input grade-input-wide"
                    type="text"
                    placeholder="e.g. Art Education"
                    value={a}
                    onChange={(e) => updateArea(idx, e.target.value)}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="grade-remove-btn"
                    onClick={() => removeArea(idx)}
                    title="Remove"
                  >
                    &times;
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grade-settings-actions">
        <button type="button" className="grade-add-btn" onClick={addArea}>
          + Add Area
        </button>
        <button type="button" className="grade-save-btn" onClick={handleSaveAreas} disabled={savingAreas}>
          {savingAreas ? 'Saving...' : 'Save Co-Scholastic Areas'}
        </button>
      </div>

      <h3 className="grade-settings-subhead">Report Card Signatures</h3>
      <p className="grade-settings-help">
        Up to {MAX_SIGNATURES} signatures printed at the bottom of report cards. Order = position:
        1 signature prints on the right, 2 print left &amp; right, 3 print left, center &amp; right,
        4 spread left to right. Use ↑ ↓ to reorder. Uploading an image is optional —
        without one, only the line and label print.
      </p>
      <p className="grade-settings-help">
        Current layout: <strong>{SIG_LAYOUTS[sigs.length] || 'none'}</strong>
      </p>

      <div className="grade-table-wrap">
        <table className="grade-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Label</th>
              <th>Signature Image</th>
              <th>Border</th>
              <th>Order</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sigs.length === 0 && (
              <tr>
                <td colSpan="6" className="grade-groups-empty">No signatures configured — cards print without signatures.</td>
              </tr>
            )}
            {sigs.map((s, idx) => (
              <tr key={idx}>
                <td className="sig-pos">{idx + 1}</td>
                <td>
                  <input
                    className="results-input grade-input"
                    type="text"
                    placeholder="e.g. Principal"
                    value={s.label}
                    onChange={(e) => updateSig(idx, 'label', e.target.value)}
                  />
                </td>
                <td>
                  <div className="sig-image-cell">
                    {(s.newImage || s.url) && (
                      <img
                        className="sig-preview"
                        src={s.newImage ? s.newImage.base64 : s.url}
                        alt={s.label || 'signature'}
                      />
                    )}
                    <label className="class-picker-btn sig-upload-btn">
                      {(s.newImage || s.url) ? 'Change image' : 'Upload image'}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => { handleSigFile(idx, e.target.files[0]); e.target.value = ''; }}
                      />
                    </label>
                  </div>
                </td>
                <td className="sig-border">
                  <label className="sig-border-toggle" title="Dotted gray border around the signature on the card">
                    <input
                      type="checkbox"
                      checked={s.border}
                      onChange={(e) => updateSig(idx, 'border', e.target.checked)}
                    />
                    <span>{s.border ? 'On' : 'Off'}</span>
                  </label>
                </td>
                <td className="sig-order">
                  <button type="button" className="grade-remove-btn" onClick={() => moveSig(idx, -1)} disabled={idx === 0} title="Move up">↑</button>
                  <button type="button" className="grade-remove-btn" onClick={() => moveSig(idx, 1)} disabled={idx === sigs.length - 1} title="Move down">↓</button>
                </td>
                <td>
                  <button
                    type="button"
                    className="grade-remove-btn"
                    onClick={() => removeSig(idx)}
                    title="Remove"
                  >
                    &times;
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grade-settings-actions">
        <button type="button" className="grade-add-btn" onClick={addSig} disabled={sigs.length >= MAX_SIGNATURES}>
          + Add Signature
        </button>
        <button type="button" className="grade-save-btn" onClick={handleSaveSigs} disabled={savingSigs}>
          {savingSigs ? 'Saving...' : 'Save Signatures'}
        </button>
      </div>
    </div>
  );
};

export default GradeSettings;
